import { useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/integrated-auth';
import { ChatMessageList } from '../chat/chat-message-list';
import { ChatInput } from '../chat/chat-input';
import { ChatReportModal } from '../chat/chat-report-modal';
import { PersonalMutesPanel } from '../chat/personal-mutes-panel';
import { useDailyRoundChat, type ChatMessage, type GifData, type TrackData } from '../chat/hooks/use-daily-round-chat';
import { useChatStatus } from '../chat/hooks/use-chat-status';
import { usePersonalMutes } from '../chat/hooks/use-personal-mutes';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Shield, Loader2, Globe, MessageCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

interface DailyLobbyChatProps {
  roundId: number;
  userId: string | null;
  username: string | null;
  avatarUrl: string | null;
  fullScreen?: boolean;
}

export function DailyLobbyChat({
  roundId,
  userId,
  username,
  avatarUrl,
  fullScreen = false,
}: DailyLobbyChatProps) {
  const { user, firebaseUser } = useAuth();
  const { toast } = useToast();
  const [reportingMessage, setReportingMessage] = useState<ChatMessage | null>(null);

  const { data: chatStatus, isLoading: statusLoading } = useChatStatus();

  const currentUserId = userId || firebaseUser?.uid || null;
  const chatUserId = firebaseUser?.uid || null;
  const isFullyAuthenticated = !!(currentUserId && (username || user?.username));
  const currentUsername = username || user?.username || firebaseUser?.displayName || null;
  const currentAvatarUrl = avatarUrl || user?.avatarImageUrl || firebaseUser?.photoURL || null;

  const chatHookEnabled = !chatStatus?.isBanned && isFullyAuthenticated;
  const {
    messages,
    isLoading: chatLoading,
    error: chatError,
    sendMessage,
    isSending,
  } = useDailyRoundChat({
    roundId,
    userId: currentUserId,
    username: currentUsername,
    avatarUrl: currentAvatarUrl,
    enabled: chatHookEnabled,
  });

  const {
    mutedUserIds: personalMutedUserIds,
    muteUser: personalMuteUser,
  } = usePersonalMutes();

  const filteredMessages = useMemo(() => {
    if (personalMutedUserIds.length === 0) return messages;
    return messages.filter(msg => !personalMutedUserIds.includes(msg.userId));
  }, [messages, personalMutedUserIds]);

  const handlePersonalMuteUser = useCallback(
    async (targetUserId: string, targetUsername: string) => {
      try {
        await personalMuteUser(targetUserId);
        toast({
          title: 'User muted',
          description: `${targetUsername}'s messages will be hidden from you.`,
        });
      } catch (error) {
        toast({
          title: 'Failed to mute user',
          description: 'Please try again',
          variant: 'destructive',
        });
      }
    },
    [personalMuteUser, toast]
  );

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!isFullyAuthenticated || !currentUsername) {
        throw new Error('Please wait for your profile to load');
      }
      try {
        await sendMessage({ content });
      } catch (error: any) {
        toast({
          title: 'Unable to send message',
          description: error?.message || 'Please try refreshing the page.',
          variant: 'destructive',
        });
        throw error;
      }
    },
    [sendMessage, isFullyAuthenticated, currentUsername, toast]
  );

  const handleSendGif = useCallback(
    async (gifData: GifData, comment?: string) => {
      if (!isFullyAuthenticated || !currentUsername) return;
      try {
        await sendMessage({ content: comment || '', contentType: 'gif', gifData });
      } catch (error: any) {
        toast({ title: 'Failed to send GIF', description: error?.message || 'Please try again', variant: 'destructive' });
      }
    },
    [sendMessage, isFullyAuthenticated, currentUsername, toast]
  );

  const handleSendTrack = useCallback(
    async (trackData: TrackData, comment?: string) => {
      if (!isFullyAuthenticated || !currentUsername) return;
      try {
        await sendMessage({ content: comment || '', contentType: 'track', trackData });
      } catch (error: any) {
        toast({ title: 'Failed to share track', description: error?.message || 'Please try again', variant: 'destructive' });
      }
    },
    [sendMessage, isFullyAuthenticated, currentUsername, toast]
  );

  const handleReport = useCallback(
    async (category: string, description?: string) => {
      if (!reportingMessage) return;
      let contentForReport = reportingMessage.content;
      if (!contentForReport || contentForReport.trim() === '') {
        contentForReport = `[${reportingMessage.contentType} message]`;
      }
      try {
        await apiRequest('/api/chat/report', 'POST', {
          messageId: reportingMessage.id,
          messageContent: contentForReport,
          messageAuthorId: reportingMessage.userId,
          category,
          description,
          context: 'daily_lobby',
          roundId,
        });
        toast({ title: 'Report submitted', description: 'Thank you for helping keep our community safe.' });
        setReportingMessage(null);
      } catch (error) {
        toast({ title: 'Failed to submit report', description: 'Please try again', variant: 'destructive' });
      }
    },
    [reportingMessage, roundId, toast]
  );

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center h-[300px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (chatStatus?.isBanned) {
    return (
      <Alert variant="destructive" className="m-4">
        <Shield className="h-4 w-4" />
        <AlertTitle>Chat Access Restricted</AlertTitle>
        <AlertDescription>
          Your chat access has been suspended.
          {chatStatus.banReason && <p className="mt-1">Reason: {chatStatus.banReason}</p>}
        </AlertDescription>
      </Alert>
    );
  }

  if (!isFullyAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground p-4">
        <MessageCircle className="h-12 w-12 mb-3 opacity-50" />
        <p className="text-sm text-center">Sign in to chat in the lobby</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col bg-background ${fullScreen ? 'h-full' : 'h-[400px] rounded-lg'}`}>
      {!fullScreen && (
        <div className="flex items-center justify-between px-4 py-2 border-b">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-blue-500" />
            {messages.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {messages.length} message{messages.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <PersonalMutesPanel />
        </div>
      )}

      <ChatMessageList
        messages={filteredMessages}
        isLoading={chatLoading}
        error={chatError}
        currentUserId={chatUserId}
        isOwner={false}
        onReport={(message) => setReportingMessage(message)}
        onPersonalMuteUser={handlePersonalMuteUser}
        personalMutedUserIds={personalMutedUserIds}
      />

      <ChatInput
        onSendMessage={handleSendMessage}
        onSendGif={handleSendGif}
        onSendTrack={handleSendTrack}
        isSending={isSending}
        disabled={!isFullyAuthenticated}
        disabledReason={!currentUserId ? 'Sign in to chat' : !isFullyAuthenticated ? 'Loading profile...' : undefined}
        imagesAllowed={false}
      />

      <ChatReportModal
        open={!!reportingMessage}
        onOpenChange={(open) => !open && setReportingMessage(null)}
        message={reportingMessage}
        onReport={handleReport}
        isReporting={false}
      />
    </div>
  );
}
